import { Timestamp } from '@firebase/firestore';
import { auth } from '../firebase';
import { postsApi } from '../posts';
import { uploadBlob, thumbnailPathFor, displayPathFor, compressImage } from '../storage';
import { OUTBOX_ENABLED } from '../../constants/outbox';
import { anonWritesBlocked } from '../../constants/auth';
import { isOnline } from '../../utils/network';
import * as outboxStorage from './outboxStorage';
import type { OutboxObservationItem, OutboxObservationData } from './types';
import type { CreatePostData } from '../../types/api';
import type { Post } from '../../types';
import { createPrefixedLogger } from '../../utils/logger';

const log = createPrefixedLogger('outbox:syncEngine');

/** Breadcrumb channel for drain diagnosis, off unless localStorage has
 *  `redi:outboxDebug` = '1' (same opt-in pattern as the WebKit long-polling
 *  flag in api/firebase.ts). Read per call, so the flag can be set at any
 *  point in a session. The drain has two SILENT skip paths (offline check,
 *  held item) that made the Aug 2026 drain stall undiagnosable from the
 *  outside — these lines exist so that never happens again. */
const dbgEnabled = (): boolean => {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('redi:outboxDebug') === '1';
  } catch {
    return false;
  }
};
const dbg = (msg: string): void => {
  // leveled logger is quiet in production builds, and this channel exists
  // precisely to be readable there (behind its localStorage opt-in).
  if (dbgEnabled()) console.log(`[outbox:debug] ${msg}`);
};

/**
 * Durable offline outbox for observations (see docs/plans — Durable Offline
 * Outbox). This module owns the in-memory queue and the FIFO drain; it is
 * intentionally store-free (no zustand imports) so it stays in the `src/api`
 * layer per the house layering rule — `src/store/outboxStore.ts` wraps it
 * for React. Auth is read imperatively from `auth.currentUser` (Firebase's
 * own singleton) rather than useAuthStore, for the same reason.
 */

const MAX_ATTEMPTS = 8;
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 5 * 60 * 1000;
const JITTER_MS = 1000;
const UPLOAD_TIMEOUT_MS = 60_000;
const DRAIN_DEBOUNCE_MS = 300;

// Raised when a queued blob is missing from local storage (e.g. evicted
// under Safari's storage pressure) — a permanent failure, not a transient
// network one, so it skips the backoff/retry cycle and goes straight to
// 'failed'.
class LostBlobError extends Error {}

// In-memory queue, source of truth for subscribers; hydrate() loads it from
// disk at boot and every mutation below persists back to outboxStorage.
let items: OutboxObservationItem[] = [];

const itemListeners = new Set<(items: OutboxObservationItem[]) => void>();
const postSyncedListeners = new Set<(post: Post) => void>();

function notifyItems(): void {
  const snapshot = [...items];
  itemListeners.forEach((cb) => cb(snapshot));
}

function notifyPostSynced(post: Post): void {
  postSyncedListeners.forEach((cb) => cb(post));
}

export function subscribeItems(cb: (items: OutboxObservationItem[]) => void): () => void {
  itemListeners.add(cb);
  cb([...items]);
  return () => {
    itemListeners.delete(cb);
  };
}

export function subscribePostSynced(cb: (post: Post) => void): () => void {
  postSyncedListeners.add(cb);
  return () => {
    postSyncedListeners.delete(cb);
  };
}

/** Persist a patch onto one item (in memory + storage) and notify. No-op if the item is gone. */
async function setItemPatch(id: string, patch: Partial<OutboxObservationItem>): Promise<void> {
  const idx = items.findIndex((it) => it.id === id);
  if (idx === -1) return;
  const next = { ...items[idx], ...patch };
  items = [...items.slice(0, idx), next, ...items.slice(idx + 1)];
  notifyItems();
  try {
    await outboxStorage.putItem(next);
  } catch (err) {
    log.warn(`persist failed for ${id}`, err);
  }
}

async function removeItemAndBlobs(item: OutboxObservationItem): Promise<void> {
  items = items.filter((it) => it.id !== item.id);
  notifyItems();
  await Promise.all([
    outboxStorage
      .removeItem(item.id)
      .catch((err) => log.warn(`removeItem failed for ${item.id}`, err)),
    outboxStorage
      .removeBlob(item.id)
      .catch((err) => log.warn(`removeBlob failed for ${item.id}`, err)),
    item.hasBeforeImage
      ? outboxStorage
          .removeBlob(`${item.id}-before`)
          .catch((err) => log.warn(`removeBlob(before) failed for ${item.id}`, err))
      : Promise.resolve(),
  ]);
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

export async function enqueueObservation(args: {
  postId: string;
  authorId: string;
  imageUri: string;
  storagePath: string;
  data: OutboxObservationData;
  beforeImageUri?: string;
}): Promise<OutboxObservationItem> {
  if (!OUTBOX_ENABLED) {
    throw new Error('Offline outbox is disabled.');
  }
  const { postId, authorId, imageUri, storagePath, data, beforeImageUri } = args;

  // Compress + read to a Blob now, at enqueue time — the volatile picker
  // URI (blob:/data:) doesn't survive a reload, so the final upload bytes
  // must be captured into stored, durable Blobs before this function
  // returns. Mirrors uploadImage's compress -> fetch -> blob pipeline.
  const compressedUri = await compressImage(imageUri, 'photo');
  const blob = await (await fetch(compressedUri)).blob();
  await outboxStorage.putBlob(postId, blob);

  // Same derivation postsStore.uploadAndCreatePost uses today, so a direct
  // create and an outbox replay land the before-image at the same path.
  let beforeStoragePath: string | undefined;
  if (beforeImageUri) {
    beforeStoragePath = storagePath.replace('.jpg', '-before.jpg');
    const beforeCompressedUri = await compressImage(beforeImageUri, 'photo');
    const beforeBlob = await (await fetch(beforeCompressedUri)).blob();
    await outboxStorage.putBlob(`${postId}-before`, beforeBlob);
  }

  const item: OutboxObservationItem = {
    id: postId,
    kind: 'observation',
    authorId,
    createdAtMs: Date.now(),
    status: 'pending',
    attempts: 0,
    nextAttemptAtMs: 0,
    storagePath,
    hasBeforeImage: !!beforeImageUri,
    beforeStoragePath,
    data,
  };

  await outboxStorage.putItem(item);
  items = [...items, item];
  notifyItems();
  requestDrain();
  return item;
}

export async function hydrate(): Promise<void> {
  if (!OUTBOX_ENABLED) return;
  try {
    items = await outboxStorage.listItems();
  } catch (err) {
    log.warn('hydrate: outbox storage unavailable', err);
    items = [];
  }
  notifyItems();
  requestDrain();
}

export async function retryItem(id: string): Promise<void> {
  await setItemPatch(id, {
    status: 'pending',
    attempts: 0,
    nextAttemptAtMs: 0,
    lastError: undefined,
  });
  requestDrain();
}

export async function deleteItem(id: string): Promise<void> {
  const item = items.find((it) => it.id === id);
  if (!item) return;
  await removeItemAndBlobs(item);
}

// ---------------------------------------------------------------------------
// Drain: debounced, single-flight FIFO processing.
// ---------------------------------------------------------------------------

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let followUpTimer: ReturnType<typeof setTimeout> | null = null;
let drainInFlight: Promise<void> | null = null;
let runAgainAfterDrain = false;

export function requestDrain(): void {
  if (!OUTBOX_ENABLED) return;
  dbg(`drain requested (debounced=${debounceTimer != null})`);
  if (debounceTimer) return; // a drain is already scheduled
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    runDrain();
  }, DRAIN_DEBOUNCE_MS);
}

function runDrain(): void {
  if (drainInFlight) {
    runAgainAfterDrain = true;
    return;
  }
  drainInFlight = drainOnce()
    .catch((err) => log.error('drain failed', err))
    .finally(() => {
      drainInFlight = null;
      if (runAgainAfterDrain) {
        runAgainAfterDrain = false;
        requestDrain();
      }
    });
}

/** Schedule a follow-up drain at the earliest nextAttemptAtMs among retryable items. */
function scheduleFollowUp(): void {
  if (followUpTimer) {
    clearTimeout(followUpTimer);
    followUpTimer = null;
  }
  // Only items backed off into the FUTURE need a timer. Items that are
  // eligible right now but still queued were either held (auth mismatch) or
  // skipped behind a failed item this pass — re-draining them immediately
  // would busy-loop on held items and defeat stop-on-first-failure's FIFO
  // ordering; they wait for an external trigger (sign-in, online,
  // foreground) or an earlier item's backoff timer.
  const now = Date.now();
  const future = items.filter((it) => it.status !== 'failed' && it.nextAttemptAtMs > now);
  if (future.length === 0) return;
  const nextAt = Math.min(...future.map((it) => it.nextAttemptAtMs));
  followUpTimer = setTimeout(() => {
    followUpTimer = null;
    requestDrain();
  }, nextAt - now);
}

async function drainOnce(): Promise<void> {
  if (!isOnline()) {
    dbg('drainOnce: skipped — isOnline() false (navigator.onLine)');
    return;
  }
  const now = Date.now();
  const eligible = items
    .filter((it) => it.status !== 'failed' && it.nextAttemptAtMs <= now)
    .sort((a, b) => a.createdAtMs - b.createdAtMs);
  dbg(`drainOnce: items=${items.length} eligible=${eligible.length}`);

  for (const item of eligible) {
    // Re-read: an earlier iteration of this same pass can't touch a later
    // item (drain stops on first failure), but a concurrent deleteItem/
    // retryItem call (e.g. from the UI) could have — skip if it's gone.
    const current = items.find((it) => it.id === item.id);
    if (!current) continue;

    const user = auth.currentUser;
    if (!user || user.uid !== current.authorId || anonWritesBlocked(user)) {
      // Held, not failed: leave it pending for a future drain under the
      // right account, and don't let a mismatched item block the rest of
      // the queue.
      dbg(
        `drainOnce: held ${current.id} — user=${user?.uid ?? 'none'} author=${current.authorId} ` +
          `anonBlocked=${user ? String(anonWritesBlocked(user)) : 'n/a'}`
      );
      continue;
    }

    dbg(`drainOnce: syncing ${current.id} (attempt ${current.attempts + 1})`);
    await setItemPatch(current.id, { status: 'uploading' });
    try {
      const post = await syncItem(current);
      await removeItemAndBlobs(current);
      dbg(`drainOnce: synced ${current.id}`);
      notifyPostSynced(post);
    } catch (err) {
      dbg(`drainOnce: failed ${current.id} — ${err instanceof Error ? err.message : String(err)}`);
      await handleItemFailure(current, err);
      // Stop on first failure: preserves FIFO order and avoids hammering a
      // dead network for the rest of the queue.
      break;
    }
  }
  // Unconditionally, not just on failure: an item already backed off from
  // an earlier failure can still be sitting in the queue with a future
  // nextAttemptAtMs after this pass finishes cleanly (e.g. it was skipped
  // by the `nextAttemptAtMs <= now` filter above while other items synced
  // fine) — without this, nothing would ever re-check it.
  scheduleFollowUp();
}

async function syncItem(item: OutboxObservationItem): Promise<Post> {
  let uploaded = item.uploaded;
  if (!uploaded) {
    const blob = await outboxStorage.getBlob(item.id);
    if (!blob) {
      throw new LostBlobError(`Photo for ${item.id} was lost from local storage.`);
    }
    const primary = await withTimeout(
      uploadBlob(blob, item.storagePath),
      UPLOAD_TIMEOUT_MS,
      'upload'
    );

    let before: { downloadUrl: string; storagePath: string } | null = null;
    if (item.hasBeforeImage && item.beforeStoragePath) {
      const beforeBlob = await outboxStorage.getBlob(`${item.id}-before`);
      if (!beforeBlob) {
        throw new LostBlobError(`Before-photo for ${item.id} was lost from local storage.`);
      }
      before = await withTimeout(
        uploadBlob(beforeBlob, item.beforeStoragePath),
        UPLOAD_TIMEOUT_MS,
        'before-upload'
      );
    }

    uploaded = {
      imageUrl: primary.downloadUrl,
      imagePath: primary.storagePath,
      thumbnailPath: thumbnailPathFor(primary.storagePath) ?? undefined,
      displayPath: displayPathFor(primary.storagePath) ?? undefined,
      ...(before
        ? {
            beforeImageUrl: before.downloadUrl,
            beforeImagePath: before.storagePath,
            beforeThumbnailPath: thumbnailPathFor(before.storagePath) ?? undefined,
          }
        : {}),
    };
    // Persist immediately so a doc-write failure below doesn't re-upload on
    // the next attempt.
    await setItemPatch(item.id, { uploaded });
  }

  const { beforePostCreatedAtMs, ...rest } = item.data;
  const fullData: CreatePostData = {
    ...rest,
    ...(beforePostCreatedAtMs !== undefined
      ? { beforePostCreatedAt: Timestamp.fromMillis(beforePostCreatedAtMs) }
      : {}),
    imageUrl: uploaded.imageUrl,
    imagePath: uploaded.imagePath,
    thumbnailPath: uploaded.thumbnailPath,
    displayPath: uploaded.displayPath,
    beforeImageUrl: uploaded.beforeImageUrl,
    beforeImagePath: uploaded.beforeImagePath,
    beforeThumbnailPath: uploaded.beforeThumbnailPath,
  };

  // replay: a drain may be re-running a create whose ACK was lost, so the
  // existence guard has to run here to avoid double-counting. This is the
  // only caller that needs it.
  return await withTimeout(
    postsApi.createPost(fullData, item.id, { replay: true }),
    UPLOAD_TIMEOUT_MS,
    'createPost'
  );
}

async function handleItemFailure(item: OutboxObservationItem, err: unknown): Promise<void> {
  const message = err instanceof Error ? err.message : String(err);
  if (err instanceof LostBlobError) {
    await setItemPatch(item.id, { status: 'failed', lastError: message });
    return;
  }
  const attempts = item.attempts + 1;
  const backoffMs =
    Math.min(2 ** attempts * BASE_BACKOFF_MS, MAX_BACKOFF_MS) + Math.random() * JITTER_MS;
  await setItemPatch(item.id, {
    attempts,
    status: attempts >= MAX_ATTEMPTS ? 'failed' : 'pending',
    nextAttemptAtMs: Date.now() + backoffMs,
    lastError: message,
  });
}
